import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Eye, BadgeCheck, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { values } from '../data/mock';

const iconMap = {
  Scale,
  Eye,
  BadgeCheck
};

export const ValuesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Unsere Werte</h1>
            <p className="text-lg text-gray-300">
              Diese drei Säulen bilden das Fundament unserer täglichen Arbeit. Sie sind der Grund, 
              warum Mandanten und Versicherungen seit Jahren auf unsere Expertise vertrauen.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {values.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={value.id} className={`grid lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}>
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <div className="w-16 h-16 bg-forest-100 rounded-xl flex items-center justify-center mb-6">
                      <IconComponent className="h-8 w-8 text-forest-700" />
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                      {value.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>

                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <Card className="bg-slate-50 border-0 shadow-lg p-8">
                      <CardContent className="p-0">
                        <div className="space-y-6">
                          {index === 0 && (
                            <>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Keine Interessenkonflikte</div>
                                  <p className="text-gray-600 text-sm">Wir arbeiten weder für Versicherungen noch für Werkstätten. Ihre Interessen stehen im Mittelpunkt.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Anerkannte Standards</div>
                                  <p className="text-gray-600 text-sm">Alle Bewertungen erfolgen nach Schwacke, DAT und weiteren anerkannten Bewertungsverfahren.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Sachliche Dokumentation</div>
                                  <p className="text-gray-600 text-sm">Faktenbasierte Gutachten ohne subjektive Bewertungen oder parteiische Interpretationen.</p>
                                </div>
                              </div>
                            </>
                          )}
                          {index === 1 && (
                            <>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Verständliche Gutachten</div>
                                  <p className="text-gray-600 text-sm">Klare Struktur und nachvollziehbare Argumentation ohne unnötiges Fachchinesisch.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Offene Kommunikation</div>
                                  <p className="text-gray-600 text-sm">Wir erklären Ihnen jeden Schritt und beantworten alle Fragen ausführlich.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Lückenlose Dokumentation</div>
                                  <p className="text-gray-600 text-sm">Jeder Bewertungsschritt ist nachvollziehbar dokumentiert und belegt.</p>
                                </div>
                              </div>
                            </>
                          )}
                          {index === 2 && (
                            <>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Schnelle Lieferung</div>
                                  <p className="text-gray-600 text-sm">Ihr Gutachten erhalten Sie schnellstmöglich in digitaler Form.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Bundesweite Akzeptanz</div>
                                  <p className="text-gray-600 text-sm">Unsere Gutachten werden von Versicherungen bundesweit akzeptiert und anerkannt.</p>
                                </div>
                              </div>
                              <div className="flex items-start gap-4">
                                <div className="w-2 h-2 bg-forest-600 rounded-full mt-2"></div>
                                <div>
                                  <div className="font-semibold text-slate-900 mb-1">Langjährige Expertise</div>
                                  <p className="text-gray-600 text-sm">Über 15 Jahre Erfahrung und mehr als 5000 erstellte Gutachten.</p>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Vertrauen durch Qualität
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Unsere Werte sind nicht nur Worte – sie spiegeln sich in jedem Gutachten wider, 
              das wir erstellen. Das ist der Grund, warum unsere Mandanten uns weiterempfehlen.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="text-4xl font-bold text-forest-700 mb-2">98%</div>
                <p className="text-gray-600">Weiterempfehlungsrate</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="text-4xl font-bold text-forest-700 mb-2">100%</div>
                <p className="text-gray-600">Versicherungskonform</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="text-4xl font-bold text-forest-700 mb-2">Premium</div>
                <p className="text-gray-600">Qualitätsstandard</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-forest-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Erleben Sie unsere Werte selbst</h2>
          <p className="text-lg text-forest-100 mb-8">
            Kontaktieren Sie uns für eine kostenlose Erstberatung.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-forest-700 hover:bg-gray-100">
              Jetzt Termin vereinbaren
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
